import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import Compose from './routes/Mode/Compose';
import createRoutes from './routes';
import appInit from './appInit';
import OpenIdConnectRoutes from './utils/OpenIdConnectRoutes';

// import {
//   ExtensionManager,
//   CommandsManager,
//   HotkeysManager,
//   ServiceProvidersManager,
// } from '@ohif/core';

import {
  DialogProvider,
  Modal,
  ModalProvider,
  ThemeWrapper,
  ViewportDialogProvider,
  ViewportGridProvider,
  CineProvider,
  UserAuthenticationProvider,
  ToolboxProvider,
} from '@ohif/ui';

import {
  ThemeWrapper as ThemeWrapperNext,
  NotificationProvider,
  TooltipProvider,
} from '@ohif/ui-next';

import { AppConfigProvider } from '@state';
import { ShepherdJourneyProvider } from 'react-shepherd';
import i18n from '@ohif/i18n';

let commandsManager, extensionManager, servicesManager, serviceProvidersManager, hotkeysManager;

async function fetchPACSConfig(config) {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const StudyInstanceUIDs = urlParams.get('StudyInstanceUIDs');

    if (config.RISE_ENTEGRASYON && StudyInstanceUIDs) {
      const requestUrl = `${config.RISE_SERVICE}pacs/url/${StudyInstanceUIDs}`;
      // console.log('🛠 Fetching PACS URLs from:', requestUrl);

      const response = await fetch(requestUrl);
      const res = await response.json();

      // console.log('🚀 Received PACS URLs:', res);

      const formatURL = (base, path) => new URL(path, base).href;

      window.config.dataSources = [
        {
          friendlyName: 'PACS',
          namespace: '@ohif/extension-default.dataSourcesModule.dicomweb',
          sourceName: 'dicomweb',
          configuration: {
            name: 'PACS',
            wadoUriRoot: formatURL(config.RISE_SERVICE, res.wadoUriRoot),
            qidoRoot: formatURL(config.RISE_SERVICE, res.qidoRoot),
            wadoRoot: formatURL(config.RISE_SERVICE, res.wadoRoot),
            qidoSupportsIncludeField: true,
            imageRendering: 'wadouri',
            thumbnailRendering: 'wadouri',
            enableStudyLazyLoad: true,
          },
        },
      ];
    }
  } catch (error) {
    console.error('❌ Error fetching PACS URLs:', error);
  }
}

function App({ config, defaultExtensions = [], defaultModes = [] }) {
  const [init, setInit] = useState(null);

  useEffect(() => {
    async function initializeApp() {
      try {
        await fetchPACSConfig(window.config || {});
        const initializedApp = await appInit(config, defaultExtensions, defaultModes);
        setInit(initializedApp);
      } catch (error) {
        console.error(error);
      }
    }

    initializeApp();
  }, []);

  useEffect(() => {
    const keydownHandler = event => {
      window.parent.postMessage(
        {
          source: 'iframe',
          keyCode: event.keyCode,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          type: 'keydown',
        },
        '*'
      );
    };

    const keyupHandler = event => {
      window.parent.postMessage(
        {
          source: 'iframe',
          keyCode: event.keyCode,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          type: 'keyup',
        },
        '*'
      );
    };

    window.addEventListener('keydown', keydownHandler);
    window.addEventListener('keyup', keyupHandler);

    return () => {
      window.removeEventListener('keydown', keydownHandler);
      window.removeEventListener('keyup', keyupHandler);
    };
  }, []);

  if (!init) {
    return null; // ✅ Yüklenme sürecini kontrol ediyoruz
  }

  commandsManager = init.commandsManager;
  extensionManager = init.extensionManager;
  servicesManager = init.servicesManager;
  serviceProvidersManager = init.serviceProvidersManager;
  hotkeysManager = init.hotkeysManager;

  const appConfigState = init.appConfig;
  const { routerBasename, modes, dataSources, oidc, showStudyList } = appConfigState;

  const {
    uiDialogService,
    uiModalService,
    uiViewportDialogService,
    viewportGridService,
    cineService,
    userAuthenticationService,
    uiNotificationService,
    customizationService,
  } = servicesManager.services;

  customizationService.init(extensionManager);

  const providers = [
    [AppConfigProvider, { value: appConfigState }],
    [UserAuthenticationProvider, { service: userAuthenticationService }],
    [I18nextProvider, { i18n }],
    [ThemeWrapperNext],
    [ThemeWrapper],
    [ToolboxProvider],
    [ViewportGridProvider, { service: viewportGridService }],
    [ViewportDialogProvider, { service: uiViewportDialogService }],
    [CineProvider, { service: cineService }],
    [NotificationProvider, { service: uiNotificationService }],
    [TooltipProvider],
    [DialogProvider, { service: uiDialogService }],
    [ModalProvider, { service: uiModalService, modal: Modal }],
    [ShepherdJourneyProvider],
  ];

  Object.entries(serviceProvidersManager.providers).forEach(([serviceName, provider]) => {
    providers.push([provider, { service: servicesManager.services[serviceName] }]);
  });

  const CombinedProviders = ({ children }) => Compose({ components: providers, children });

  const appRoutes = createRoutes({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    routerBasename,
    showStudyList,
  });

  return (
    <CombinedProviders>
      <BrowserRouter basename={routerBasename}>
        {oidc && (
          <OpenIdConnectRoutes
            oidc={oidc}
            routerBasename={routerBasename}
            userAuthenticationService={userAuthenticationService}
          />
        )}
        {appRoutes}
      </BrowserRouter>
    </CombinedProviders>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
  defaultExtensions: PropTypes.array,
  defaultModes: PropTypes.array,
};

export default App;
export { commandsManager, extensionManager, servicesManager };
